// Implements: F-UIX-017, F-UIX-007, F-UIX-015
/**
 * The versions before and after the one on screen. Pure presentation.
 *
 * Real links, so a mouse, Tab and Enter reach them as well as the shortcut does; each one
 * names its shortcut (`aria-keyshortcuts`) and the hint says it in words, so the keyboard
 * way is discoverable without a manual. A version with neither neighbour shows nothing.
 */

import { useId } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { keyShortcutLabel } from "../../../shared/ui/useKeyShortcut";
import type { KeyShortcut } from "../../../shared/ui/useKeyShortcut";
import type { VersionTreeNode } from "../../../types/api";
import styles from "./VersionStepper.module.css";

/** Alt+Shift+Arrow: Alt+Arrow alone is the browser's back and forward, so it is left alone. */
export const PREVIOUS_VERSION_SHORTCUT: KeyShortcut = { key: "ArrowLeft", alt: true, shift: true };
export const NEXT_VERSION_SHORTCUT: KeyShortcut = { key: "ArrowRight", alt: true, shift: true };

export function versionPath(mockupId: string, versionId: string): string {
  return `/mockups/${encodeURIComponent(mockupId)}/versions/${encodeURIComponent(versionId)}`;
}

export interface VersionStepperProps {
  readonly mockupId: string;
  readonly previous: VersionTreeNode | null;
  readonly next: VersionTreeNode | null;
}

export function VersionStepper({ mockupId, previous, next }: VersionStepperProps): ReactElement | null {
  const { t } = useTranslation();
  const hintId = useId();
  if (previous === null && next === null) return null;

  return (
    <nav className={styles.stepper} aria-label={t("version.switch.label")}>
      {previous === null ? null : (
        <Link
          to={versionPath(mockupId, previous.id)}
          aria-keyshortcuts={keyShortcutLabel(PREVIOUS_VERSION_SHORTCUT)}
          aria-describedby={hintId}
        >
          {t("version.switch.previous", { n: previous.sequence_number })}
        </Link>
      )}
      {next === null ? null : (
        <Link
          to={versionPath(mockupId, next.id)}
          aria-keyshortcuts={keyShortcutLabel(NEXT_VERSION_SHORTCUT)}
          aria-describedby={hintId}
        >
          {t("version.switch.next", { n: next.sequence_number })}
        </Link>
      )}
      <p id={hintId} className={styles.hint}>
        {t("version.switch.hint")}
      </p>
    </nav>
  );
}
